/**
 * Utility functions for version comparison with comprehensive edge case handling
 */

/**
 * Validates if a version string is in a valid format
 * @param version - Version string to validate
 * @returns boolean indicating if the version is valid
 */
export const isValidVersionString = (version: string): boolean => {
  if (!version || typeof version !== 'string') {
    return false;
  }

  // Trim whitespace and check for basic version pattern
  const trimmed = version.trim();

  // Allow semantic versioning with optional pre-release/build metadata
  // Examples: "1.0.0", "1.2.3-beta", "1.2.3-alpha.1", "1.2.3+build.1"
  const versionRegex = /^(\d+)\.(\d+)\.(\d+)(?:[-+].*)?$/;

  if (!versionRegex.test(trimmed)) {
    // Also allow 2-part versions like "1.0"
    const simpleVersionRegex = /^(\d+)\.(\d+)(?:[-+].*)?$/;
    return simpleVersionRegex.test(trimmed);
  }

  return true;
};

/**
 * Normalizes a version string by removing pre-release and build metadata
 * @param version - Version string to normalize
 * @returns normalized version string
 */
export const normalizeVersion = (version: string): string => {
  if (!version || typeof version !== 'string') {
    return '0.0.0';
  }

  // Trim whitespace
  const trimmed = version.trim();

  // Remove pre-release and build metadata (everything after - or +)
  const coreVersion = trimmed.split(/[-+]/)[0];

  // Split into parts and ensure we have at least major.minor.patch
  const parts = coreVersion.split('.');

  // Ensure we have exactly 3 parts (major.minor.patch)
  while (parts.length < 3) {
    parts.push('0');
  }

  // Take only the first 3 parts and ensure they're valid numbers
  const normalizedParts = parts.slice(0, 3).map((part) => {
    const num = parseInt(part, 10);
    return Number.isNaN(num) ? 0 : num;
  });

  return normalizedParts.join('.');
};

/**
 * Enhanced version comparison function with comprehensive edge case handling
 * @param currentVersion - The current app version
 * @param minimumVersion - The minimum required version
 * @returns boolean - True if the current version is greater than or equal to the minimum version
 */
export const isAppVersionValid = (currentVersion: string, minimumVersion: string): boolean => {
  // Input validation
  if (!currentVersion || !minimumVersion) {
    console.warn('Invalid version input:', { currentVersion, minimumVersion });
    return false;
  }

  if (typeof currentVersion !== 'string' || typeof minimumVersion !== 'string') {
    console.warn('Version inputs must be strings:', { currentVersion, minimumVersion });
    return false;
  }

  // Validate version format
  if (!isValidVersionString(currentVersion)) {
    console.warn('Invalid current version format:', currentVersion);
    return false;
  }

  if (!isValidVersionString(minimumVersion)) {
    console.warn('Invalid minimum version format:', minimumVersion);
    return false;
  }

  // Normalize versions (remove pre-release/build metadata and ensure 3 parts)
  const normalizedCurrent = normalizeVersion(currentVersion);
  const normalizedMinimum = normalizeVersion(minimumVersion);

  // Parse version parts
  const currentParts = normalizedCurrent.split('.').map((part) => parseInt(part, 10));
  const minimumParts = normalizedMinimum.split('.').map((part) => parseInt(part, 10));

  // Compare each part (major, minor, patch)
  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < Math.max(currentParts.length, minimumParts.length); i++) {
    const current = currentParts[i] || 0;
    const minimum = minimumParts[i] || 0;

    // Double-check for NaN (shouldn't happen after normalization, but safety first)
    if (Number.isNaN(current) || Number.isNaN(minimum)) {
      console.warn('NaN detected in version comparison:', { current, minimum, i });
      return false;
    }

    if (current > minimum) {
      return true;
    }
    if (current < minimum) {
      return false;
    }
  }

  return true; // Versions are equal
};

/**
 * Legacy version comparison function for backward compatibility
 * This is the original implementation with minimal safety checks
 * @deprecated Use isAppVersionValid instead
 */
export const isAppVersionValidLegacy = (
  currentVersion: string,
  minimumVersion: string
): boolean => {
  // Basic null/undefined check
  if (!currentVersion || !minimumVersion) {
    return false;
  }

  const currentParts = currentVersion.split('.').map(Number);
  const minimumParts = minimumVersion.split('.').map(Number);

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < Math.max(currentParts.length, minimumParts.length); i++) {
    const current = currentParts[i] || 0;
    const minimum = minimumParts[i] || 0;

    // Check for NaN
    if (Number.isNaN(current) || Number.isNaN(minimum)) {
      return false;
    }

    if (current > minimum) {
      return true;
    }
    if (current < minimum) {
      return false;
    }
  }
  return true; // Versions are equal
};

/**
 * Comprehensive test cases for version comparison
 */
export const runVersionComparisonTests = (): void => {
  const testCases = [
    // Standard cases
    { current: '1.0.0', minimum: '1.0.0', expected: true, description: 'Equal versions' },
    { current: '1.0.1', minimum: '1.0.0', expected: true, description: 'Higher patch version' },
    { current: '1.1.0', minimum: '1.0.0', expected: true, description: 'Higher minor version' },
    { current: '2.0.0', minimum: '1.0.0', expected: true, description: 'Higher major version' },
    { current: '1.0.0', minimum: '1.0.1', expected: false, description: 'Lower patch version' },
    { current: '1.0.0', minimum: '1.1.0', expected: false, description: 'Lower minor version' },
    { current: '1.0.0', minimum: '2.0.0', expected: false, description: 'Lower major version' },

    // Edge cases
    { current: '1.0', minimum: '1.0.0', expected: true, description: '2-part vs 3-part version' },
    { current: '1.0.0', minimum: '1.0', expected: true, description: '3-part vs 2-part version' },
    { current: '1.0.0-beta', minimum: '1.0.0', expected: true, description: 'Pre-release version' },
    { current: '1.0.0+build.1', minimum: '1.0.0', expected: true, description: 'Build metadata' },
    { current: ' 1.0.0 ', minimum: '1.0.0', expected: true, description: 'Whitespace handling' },

    // Invalid cases (should return false)
    { current: '', minimum: '1.0.0', expected: false, description: 'Empty current version' },
    { current: '1.0.0', minimum: '', expected: false, description: 'Empty minimum version' },
    {
      current: 'invalid',
      minimum: '1.0.0',
      expected: false,
      description: 'Invalid current version',
    },
    {
      current: '1.0.0',
      minimum: 'invalid',
      expected: false,
      description: 'Invalid minimum version',
    },
    {
      current: '1.a.0',
      minimum: '1.0.0',
      expected: false,
      description: 'Non-numeric version parts',
    },
  ];

  console.log('Running version comparison tests...');

  testCases.forEach(({ current, minimum, expected, description }) => {
    const result = isAppVersionValid(current, minimum);
    const passed = result === expected;

    console.log(
      `${passed ? '✅' : '❌'} ${description}: isAppVersionValid('${current}', '${minimum}') = ${result} (expected: ${expected})`
    );
  });
};
